/**
 * `engine/axe-produit.ts` — **L'axe Produit ↔ Authentique** (SPEC-AXE-PRODUIT, ⟦A-4 B⟧).
 *
 * Le second axe d'identité de la chaîne. L'image de chaîne dit ce que tu **racontes** ; cet
 * axe-ci dit comment tu **fabriques** : l'argent que tu remets dans la production, les étapes
 * que tu délègues, le matériel que tu possèdes.
 *
 * ── La règle qui tient tout, mot pour mot ⟦A-4 B⟧ ──────────────────────────────
 *
 * **« Ni bonus ni malus mécanique direct — l'axe pilote du contenu, pas la formule. »**
 *
 * Ce module n'écrit **aucune** jauge : ni abonnés, ni vues, ni argent, ni Fidélité. Il lit
 * l'état et pose trois champs qui n'appartiennent qu'à lui (`produit_authentique`,
 * `pa_journal`, `pa_reference`), deux drapeaux et deux compteurs. Sa seule sortie vers le
 * reste du jeu passe par les **conditions d'événements** (`produit_min` / `produit_max`,
 * `derive_produit` / `derive_artisanale`) et par l'affichage.
 *
 * Conséquence directe, et c'est la garantie structurelle de SPEC-AXE-PRODUIT §0 : **un axe
 * sans effet mécanique ne peut pas déplacer un chiffre**. Aucune ancre de calibrage
 * (SPEC-MOTEUR §12) ne bouge parce que rien de ce qui la calcule ne lit ce fichier.
 *
 * ── Aucun tirage ⟦SPEC-AXE-PRODUIT §0⟧ ─────────────────────────────────────────
 *
 * L'axe est **entièrement déterministe** : c'est une lecture de l'état, pas un jet. Aucun
 * `draw()` n'est importé ici, et c'est vérifié par un test (T-PA-9). Le flux d'aléa du moteur
 * ne connaît donc pas ce module — il ne peut ni le décaler ni en dépendre.
 *
 * ── L'interrupteur, et pourquoi il est ÉTEINT ⟦A-57⟧ ────────────────────────────
 *
 * `content/meta.json → axe_produit_actif` (défaut `false`). À `false`, **chaque** point
 * d'entrée public de ce module sort à sa première ligne, avant d'écrire quoi que ce soit :
 * `produit_authentique` reste `null` pour toute la run et aucune structure n'est créée. Le
 * jeu retombe au caractère près sur son comportement d'avant l'axe.
 *
 * ⚠️ **Le défaut est `false`, là où SPEC-AXE-PRODUIT §9.1 ✎ 12 écrivait `true`** — même
 * amendement, même motif et même précédent que le Fil ⟦A-57⟧ : le socle moteur est livré avant
 * les ≥ 13 événements de §6.4 (`content/events.json`). Allumer maintenant, ce serait montrer
 * au joueur un libellé de bande que rien ne vient jamais commenter. Le jour où le contenu
 * existe, c'est **un caractère à changer** — et c'était tout l'objet de l'interrupteur.
 *
 * ── État neutre ⟦A-10⟧ ─────────────────────────────────────────────────────────
 *
 * Tous les champs sont **optionnels** et leur absence vaut leur valeur neutre : aucun état
 * sérialisé n'a besoin d'être migré (§7). Ne jamais les lire en direct — passer par
 * `axeProduit`, `paJournal`, `referenceProduit`, qui savent traiter l'absence.
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucune horloge système, aucun `Math.random`, aucun `draw`.
 */
import { H_JOUR, jour } from "./clock.js";
// ─────────────────────────────────────────────────────────────────────────────
// §9.1 — Les constantes ✎ de la spec, toutes nommées, toutes exportées
// ─────────────────────────────────────────────────────────────────────────────
/** ✎ 1 — fenêtre de FLUX : les jours simulés sur lesquels S1 et S2 sont sommés (§4.3). */
export const FENETRE_PA_J = 30;
/** ✎ 2 — lissage exponentiel par nuit (§4.4). ¼ d'écart rattrapé ⇒ ≈ 10 nuits pour 95 %. */
export const ALPHA_PA = 0.25;
/** ✎ 3 — les trois poids de l'indice (§4.1). Leur somme DOIT valoir 1 (test T-PA-2). */
export const W_BUDGET = 0.4;
export const W_DELEGATION = 0.35;
export const W_MATERIEL = 0.25;
/** ✎ 4 — S1 : réinvestir la moitié de ses revenus = « pleinement produit » (§3.1). */
export const RATIO_PLEIN = 0.5;
/**
 * ✎ 4 — plancher de revenu de S1 (§3.1). **Sans lui, dépenser 50 ₵ un mois sans aucun revenu
 * vaudrait « 100 % produit »** : le ratio D/R exploserait sur un dénominateur nul.
 */
export const R_PLANCHER = 200;
/** ✎ 5 — S2 : acheter 3 étapes sur 5 d'une longue = « pleinement délégué » (§3.2). */
export const TX_PLEIN = 0.6;
/**
 * ✎ 6 — S3 : part des euros de l'arbre au-delà de laquelle le matériel est « plein » (§3.3).
 * Une run achète ~38 % de l'arbre (PLAN §4.12) : exiger 100 % rendrait S3 inatteignable.
 */
export const PART_PLEINE = 0.5;
/**
 * ✎ 7 — l'unité de temps du « il a changé » : **224 h, ~9,33 jours simulés (224 / H_JOUR)**
 * (§5).
 *
 * 🔑 **Prose corrigée, valeur inchangée — décision de Mathieu (25/08/2026, ⟦A-113 §1⟧) :**
 * *« Le nombre de jours n'a aucune incidence, c'était complètement au doigt mouillé. »* Cette
 * constante était documentée comme « voulue à 14 jours » — une confusion d'échelle : 224 ne
 * vaut 14 jours que si la journée fait 16 h (l'échelle d'avant A-19), alors que sous
 * `H_JOUR = 24` en vigueur, 224 h ne fait que 9,33 jours (14 × H_JOUR aurait fait 336).
 * ⟦A-110⟧ avait signalé l'écart sans le corriger (l'axe Produit est ACTIF depuis A-68, changer
 * la valeur aurait déplacé le rythme de détection de `derive_produit`/`derive_artisanale`,
 * donc potentiellement les six cibles A-40). ⟦A-113 §1⟧ tranche : **la valeur ne bouge pas**,
 * les jours sont le métronome de l'énergie et des événements, pas un chiffre de design — seule
 * la prose (ici et `docs/SPEC-AXE-PRODUIT.md §5/§8`) cesse de dire « 14 jours ».
 */
export const FENETRE_DERIVE_H = 224;
/** ✎ 7 — un quart de l'axe en ~9,33 jours (§5, `FENETRE_DERIVE_H`). */
export const SEUIL_DERIVE = 25;
/**
 * ✎ 8 — le temps pendant lequel le sujet reste « chaud » pour les événements : **480 h, 20
 * jours simulés exactement (480 / H_JOUR)** (§5).
 *
 * Même correction de prose que `FENETRE_DERIVE_H` ci-dessus, même décision ⟦A-110 · A-113 §1⟧ :
 * documentée un temps comme « voulue à 30 jours » en confondant l'échelle d'avant A-19
 * (480 = 30 × 16 h/jour) avec `H_JOUR = 24` en vigueur (sous laquelle 480 h vaut 20 jours, pas
 * 30 — `H_MOIS` aurait fait 720). **La valeur ne bouge pas** : « le nombre de jours n'a aucune
 * incidence, c'était complètement au doigt mouillé. »
 */
export const PA_FLAG_DUREE_H = 480;
/**
 * ✎ 8 — pas deux procès en un mois (§5) : **480 h, 20 jours simulés exactement** — pas
 * `H_MOIS` (720 h). Même correction de prose que `PA_FLAG_DUREE_H` ci-dessus ⟦A-110 · A-113
 * §1⟧ : la valeur reste 480 h, seul le nombre de jours affiché en commentaire était faux.
 */
export const PA_COOLDOWN_H = 480;
/**
 * ✎ 9 — seuil de bande, **le même que celui de l'alignement** ⟦A23⟧ : il n'y a qu'une seule
 * convention de bande à retenir dans tout le jeu (§1.1).
 */
export const BANDE_PA_SEUIL = 30;
/** Bornes de l'axe (§1.1). */
export const PA_MIN = -100;
export const PA_MAX = 100;
/**
 * Étapes d'Atelier par format (PLAN §4.5, §3.2) — le dénominateur de S2. Une longue en compte
 * **5** (écriture, tournage, montage, miniature, promotion), un short **3**.
 */
export const ETAPES_PAR_FORMAT = { short: 3, longue: 5 };
/**
 * ⟦SPEC-AXE-PRODUIT §7⟧ **L'interrupteur général.** `content.meta.axe_produit_actif !== true`
 * ⇒ l'axe n'existe pas : aucun calcul, aucun champ écrit, aucun événement conditionné.
 *
 * Absent = éteint, comme `fil_actif` ⟦A-57⟧ — un contenu qui ne connaît pas la clé joue le jeu
 * d'avant l'axe, ce qui rend le socle sûr à poser dans un dépôt dont le contenu n'a pas suivi.
 */
export function axeProduitActif(content) {
    return content.meta?.axe_produit_actif === true;
}
/** Les nœuds de l'arbre vus par ce module — jamais de plantage sur un contenu partiel. */
function noeuds(content) {
    return (content.skills ?? []);
}
// ─────────────────────────────────────────────────────────────────────────────
// §1 — Les lectures d'état (toutes tolèrent l'absence)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * La valeur de l'axe, ou `null` s'il est **inactif** — avant le Grand Saut ⟦A-1⟧, ou
 * interrupteur éteint. C'est la seule lecture autorisée de `state.produit_authentique`.
 */
export function axeProduit(state) {
    return state.produit_authentique ?? null;
}
/** Le journal des flux (§1.2), `[]` à l'absence. Ne jamais lire `state.pa_journal` en direct. */
export function paJournal(state) {
    return state.pa_journal ?? [];
}
/** Le point de référence de la détection de dérive (§5), `null` à l'absence. */
export function referenceProduit(state) {
    return state.pa_reference ?? null;
}
/**
 * §1.1 — la bande d'une valeur d'axe. `null` (axe inactif) ⇒ **aucune bande** : il n'y a rien
 * à dire d'une chaîne dont on ne mesure pas encore la façon de produire.
 */
export function bandeProduit(valeur) {
    if (valeur === null)
        return null;
    if (valeur <= -BANDE_PA_SEUIL)
        return "artisanal";
    if (valeur >= BANDE_PA_SEUIL)
        return "produit";
    return "hybride";
}
/** Raccourci d'écran : la bande de l'état courant, `null` si l'axe dort. */
export function bandeDeLaChaine(state) {
    return bandeProduit(axeProduit(state));
}
function clamp(v, min, max) {
    return Math.min(max, Math.max(min, v));
}
// ─────────────────────────────────────────────────────────────────────────────
// §1.2 / §2 — Le journal : un anneau borné à FENETRE_PA_J entrées
// ─────────────────────────────────────────────────────────────────────────────
/**
 * L'entrée du jour courant, créée si besoin. **Privée** : c'est le seul endroit qui écrit
 * `state.pa_journal`, ce qui rend la borne d'⟦A38⟧ vérifiable en un point.
 *
 * ⚠️ La purge est appliquée ICI en plus de la frontière de jour : une action longue peut
 * traverser plusieurs jours, et un joueur peut dépenser plusieurs fois dans la même journée.
 * Purger au seul endroit qui écrit, c'est ce qui garantit `length ≤ FENETRE_PA_J` **entre
 * deux nuits** — la leçon de la rupture n° 1 de `docs/AUDIT-MAILLONS.md`, où le Fil bornait sa
 * file dans une fonction que l'autre chemin d'écriture n'appelait pas.
 */
function entreeDuJour(state, jourCourant) {
    const journal = state.pa_journal ?? (state.pa_journal = []);
    let entree = journal.find((e) => e.jour === jourCourant);
    if (entree === undefined) {
        entree = { jour: jourCourant, depense: 0, revenu: 0, etapes_achetees: 0, etapes_jouees: 0 };
        journal.push(entree);
    }
    purger(state, jourCourant);
    // `purger` peut avoir retiré des entrées, jamais celle du jour courant (c'est la plus
    // récente) : la référence reste valide.
    return entree;
}
/**
 * **Borne dure d'état** (§1.2, ⟦A38⟧) : l'état est cloné à chaque action et sérialisé en base,
 * donc aucune structure du moteur n'a le droit de croître avec la durée de la run.
 *
 * On purge sur DEUX critères, et il en faut deux : la fenêtre temporelle (une entrée de plus
 * de `FENETRE_PA_J` jours ne compte plus dans S1 ni S2) **et** la longueur (garde-fou si un
 * saut d'horloge anormal laissait des trous). Le second seul laisserait vivre des entrées
 * périmées ; le premier seul ne borne rien si les jours ne se suivent pas.
 */
function purger(state, jourCourant) {
    const journal = state.pa_journal;
    if (journal === undefined)
        return;
    const plusVieuxAdmis = jourCourant - FENETRE_PA_J + 1;
    let filtre = journal.filter((e) => e.jour >= plusVieuxAdmis);
    if (filtre.length > FENETRE_PA_J)
        filtre = filtre.slice(-FENETRE_PA_J);
    state.pa_journal = filtre;
}
/**
 * §2 — **à chaque frontière de jour franchie** (SPEC-MOTEUR §2.3 pas 5) : ouverture de
 * l'entrée du jour, purge au-delà de la fenêtre.
 *
 * Ouvrir une entrée vide n'est pas cosmétique : c'est ce qui fait qu'une journée sans dépense
 * **compte comme une journée sans dépense** dans la somme, et non comme une journée qui
 * n'existe pas.
 */
export function ouvrirJourneeProduit(state, content, jourAtteint) {
    if (!axeProduitActif(content))
        return;
    entreeDuJour(state, jourAtteint);
}
// ─────────────────────────────────────────────────────────────────────────────
// §3 — Les trois signaux : alimentation du journal
// ─────────────────────────────────────────────────────────────────────────────
/**
 * §3.1 — **une dépense de PRODUCTION**. La liste des sources est FERMÉE par la spec, et les
 * appelants sont les seuls à en décider : un nœud acheté (`cout_euros`), une embauche de
 * freelance ⟦A-4 E⟧, la production value ⟦A-11 §3⟧.
 *
 * Ce qui n'y entre **jamais** (§3.1) : `therapie` et `respec` (prendre soin de soi n'est pas
 * produire), la boutique de matériel de vie ⟦A-11 §4⟧, les charges fixes (subies, pas
 * choisies) et les dons caritatifs — ces derniers parce qu'⟦A-4 décision 6⟧ exige qu'ils ne
 * soient « jamais un levier d'optimisation », et les faire compter en ferait un.
 */
export function noterDepenseProduction(state, content, montant) {
    if (!axeProduitActif(content) || montant <= 0)
        return;
    entreeDuJour(state, jour(state.clock_h)).depense += montant;
}
/**
 * §3.1 — **un encaissement**. Tout ₵ crédité : tranches de courbe, rente evergreen, sponsors,
 * merch.
 *
 * ⚠️ **Le capital du Grand Saut est exclu** (✎ 11) : c'est un cadeau des parents, pas un
 * revenu. Le compter ferait chuter S1 pendant un mois entier au moment précis où l'axe
 * s'active — l'exact contraire de ce que §7 cherche à garantir.
 *
 * `jourCible` sert aux **frontières de jour** : une action longue en traverse plusieurs, et
 * l'horloge est déjà arrivée au bout quand le pas 5 les résout une à une. Sans lui, trois
 * journées de versement s'écraseraient sur le jour d'arrivée — sans changer D ni R (les
 * sommes portent sur toute la fenêtre), mais en décalant la sortie de fenêtre.
 */
export function noterRevenu(state, content, montant, jourCible) {
    if (!axeProduitActif(content) || montant <= 0)
        return;
    entreeDuJour(state, jourCible ?? jour(state.clock_h)).revenu += montant;
}
/**
 * §3.2 — **les étapes d'une publication**, réparties entre celles qui ont été achetées et
 * celles qui ont été jouées à la main.
 *
 * « Achetée » vaut pour tout mécanisme (marché des freelances, community manager, délégation
 * transitoire) : ce qui compte est que le score **ne vienne pas de la main du joueur**.
 */
export function noterEtapes(state, content, format, achetees) {
    if (!axeProduitActif(content))
        return;
    const total = ETAPES_PAR_FORMAT[format];
    const a = clamp(Math.round(achetees), 0, total);
    const entree = entreeDuJour(state, jour(state.clock_h));
    entree.etapes_achetees += a;
    entree.etapes_jouees += total - a;
}
// ─────────────────────────────────────────────────────────────────────────────
// §3 / §4 — Les trois signaux et l'indice
// ─────────────────────────────────────────────────────────────────────────────
/** §3.1 — le budget de production : ce qu'on remet dans la machine, rapporté à ce qu'elle rend. */
export function signalBudget(state) {
    let D = 0;
    let R = 0;
    for (const e of paJournal(state)) {
        D += e.depense;
        R += e.revenu;
    }
    return 100 * clamp(D / Math.max(R, R_PLANCHER) / RATIO_PLEIN, 0, 1);
}
/**
 * §3.2 — le recours aux freelances. **Aucune production dans la fenêtre ⇒ 0**, c'est-à-dire
 * neutre : une chaîne à l'arrêt n'est pas « artisanale par mérite », elle est à l'arrêt.
 */
export function signalDelegation(state) {
    let A = 0;
    let J = 0;
    for (const e of paJournal(state)) {
        A += e.etapes_achetees;
        J += e.etapes_jouees;
    }
    if (A + J === 0)
        return 0;
    return 100 * clamp(A / (A + J) / TX_PLEIN, 0, 1);
}
/**
 * §3.3 — le matériel possédé. Un **stock**, pas un flux : il ne s'oublie pas au bout de 30
 * jours.
 *
 * 🔑 **Aucune constante magique en euros.** Le dénominateur est DÉRIVÉ du contenu : ajouter un
 * nœud coûteux à `skills.json` ne déplace pas l'axe en silence, il déplace aussi le
 * dénominateur (test T-PA-11). C'est la discipline « contenu = données » de CLAUDE.md
 * appliquée à une formule.
 *
 * Division protégée (§7) : un arbre sans `cout_euros` donne `euros_arbre = 0` ⇒ S3 = 0.
 */
export function signalMateriel(state, content) {
    const arbre = noeuds(content);
    let possedes = 0;
    let total = 0;
    for (const n of arbre) {
        const euros = n.cout_euros ?? 0;
        total += euros;
        if (state.skills.includes(n.id))
            possedes += euros;
    }
    const denominateur = total * PART_PLEINE;
    if (denominateur <= 0)
        return 0;
    return 100 * clamp(possedes / denominateur, 0, 1);
}
/** §4.1 — l'indice instantané, ∈ [0 ; 100]. */
export function indiceProduit(state, content) {
    return (W_BUDGET * signalBudget(state) +
        W_DELEGATION * signalDelegation(state) +
        W_MATERIEL * signalMateriel(state, content));
}
/**
 * §4.2 — la cible, ∈ [−100 ; +100]. Un joueur qui ne dépense rien, ne délègue rien et ne
 * possède rien tombe à −100 : **artisanal**. C'est la vérité du débutant.
 */
export function cibleProduit(state, content) {
    return clamp(2 * indiceProduit(state, content) - 100, PA_MIN, PA_MAX);
}
// ─────────────────────────────────────────────────────────────────────────────
// §7 — Activation au Grand Saut ⟦A-1⟧
// ─────────────────────────────────────────────────────────────────────────────
/**
 * §7 — **l'activation**, au Grand Saut et jamais avant : sans argent, mesurer un ratio de
 * dépenses serait mesurer du vide (⟦A37⟧ : un `gauge_delta` sur `argent` est ignoré tant que
 * `grand_saut_done` est faux).
 *
 * 🔑 `pa_reference` est posée **dans le même geste**, à la valeur d'activation. Sans cette
 * ligne, l'arrivée des 600 ₵ et des premiers achats produirait un « il a changé » absurde dès
 * le palier 1 000 — le joueur se ferait reprocher d'avoir commencé à travailler.
 *
 * Idempotente et rattrapante : un état sérialisé avant l'axe (ou une run dont l'interrupteur
 * vient d'être allumé) s'active à la première action postérieure au Grand Saut.
 */
export function activerAxeProduitSiBesoin(state, content) {
    if (!axeProduitActif(content))
        return false;
    if (!state.flags.grand_saut_done)
        return false;
    if (state.produit_authentique !== undefined && state.produit_authentique !== null)
        return false;
    const valeur = cibleProduit(state, content);
    state.produit_authentique = valeur;
    state.pa_reference = { h: state.clock_h, valeur };
    return true;
}
// ─────────────────────────────────────────────────────────────────────────────
// §4.4 / §5 — Le pas de nuit : lissage puis détection de dérive
// ─────────────────────────────────────────────────────────────────────────────
/**
 * §5 — les drapeaux de dérive **expirent**. Ils ne font rien par eux-mêmes : ils rendent
 * éligibles les événements « il a changé » (§6.2). Un drapeau qui ne retomberait jamais
 * transformerait un moment en état permanent.
 *
 * ⚠️ **Écart signalé** : §1.3 décrit deux booléens et un seul compteur
 * (`pa_derive_cooldown_h`), sans dire OÙ vit l'échéance des drapeaux — or `PA_FLAG_DUREE_H` et
 * `PA_COOLDOWN_H` sont deux constantes distinctes de §5. S'appuyer sur leur égalité actuelle
 * (480 h = 480 h) rendrait la spec fausse le jour où l'une des deux bougerait. D'où
 * `pa_flag_expire_h`, un compteur de plus, optionnel comme les autres.
 */
export function purgerFlagsDerive(state) {
    const echeance = state.counters.pa_flag_expire_h;
    if (echeance === undefined)
        return;
    if (state.clock_h < echeance)
        return;
    delete state.flags.derive_produit;
    delete state.flags.derive_artisanale;
    delete state.counters.pa_flag_expire_h;
}
/**
 * §4.4 + §5 — **le pas de nuit de l'axe.** Appelé à chaque résolution de nuit, après le
 * versement de la rente (donc après que les revenus de la nuit soient au journal).
 *
 * 1. les drapeaux échus retombent ;
 * 2. lissage exponentiel vers `P_cible` — l'axe bouge d'au plus un quart de son écart ;
 * 3. détection de dérive **par PAS**, pas en fenêtre glissante.
 *
 * 🔑 **Pourquoi une référence par PAS.** ⟦A-4 B⟧ demande qu'« une évolution lente passe sans
 * commentaire ». Avec une référence remise à zéro toutes les ~9,33 jours simulés
 * (`FENETRE_DERIVE_H`, ⟦A-110 · A-113 §1⟧ — le compte en jours n'est qu'un repère, pas une
 * cible), une pente de 20 points sur cette même fenêtre ne déclenche **jamais** rien, même en
 * menant de −100 à +100 en quelques mois (test T-PA-7). Une fenêtre glissante finirait par
 * déclencher — et transformerait la professionnalisation normale en procès permanent.
 *
 * MUTE `state`. Pousse au plus un `EngineEvent` de kind `warning` — **aucun nouveau kind** :
 * l'énumération fermée de SPEC-MOTEUR §1.8 reste fermée (§5).
 */
export function nuitAxeProduit(state, content, events = []) {
    if (!axeProduitActif(content))
        return;
    purgerFlagsDerive(state);
    const courant = axeProduit(state);
    if (courant === null)
        return; // axe inactif (§7) : rien à lisser, rien à surveiller.
    // — §4.4 lissage —
    const cible = cibleProduit(state, content);
    const lisse = clamp(courant + ALPHA_PA * (cible - courant), PA_MIN, PA_MAX);
    state.produit_authentique = lisse;
    // — §5 dérive rapide —
    detecterDerive(state, events);
}
/**
 * §5 — **la détection de dérive rapide**, isolée exactement comme la spec la nomme, pour
 * qu'elle soit testable sans passer par le lissage (T-PA-6, T-PA-7).
 *
 * Appelée au sommeil, APRÈS le lissage §4.4. Trois gardes en sortie sèche : axe inactif,
 * cooldown en cours, fenêtre pas encore écoulée.
 *
 * 🔑 **La référence repart à chaque fenêtre, qu'une dérive ait été signalée ou non.** C'est ce
 * « par PAS » qui laisse passer les évolutions lentes : une pente de 20 points par quinzaine
 * ne franchit jamais les 25 points d'un pas, même en menant de −100 à +100 en quelques mois.
 * Une fenêtre glissante finirait, elle, par déclencher — et ferait de la professionnalisation
 * normale un procès permanent, ce qu'⟦A-4 B⟧ refuse mot pour mot.
 *
 * MUTE `state`. Rend la dérive signalée, ou `null`.
 */
export function detecterDerive(state, events = []) {
    const valeur = axeProduit(state);
    if (valeur === null)
        return null;
    const reference = referenceProduit(state);
    if (reference === null) {
        // Filet : un état activé sans référence (contenu allumé en cours de run) en reçoit une
        // maintenant, sans jamais déclencher de dérive rétroactive.
        state.pa_reference = { h: state.clock_h, valeur };
        return null;
    }
    if (state.clock_h < (state.counters.pa_derive_cooldown_h ?? 0))
        return null;
    if (state.clock_h - reference.h < FENETRE_DERIVE_H)
        return null;
    const derive = valeur - reference.valeur;
    let signalee = null;
    if (derive >= SEUIL_DERIVE)
        signalee = "produit";
    else if (derive <= -SEUIL_DERIVE)
        signalee = "artisanale";
    if (signalee !== null) {
        if (signalee === "produit")
            state.flags.derive_produit = true;
        else
            state.flags.derive_artisanale = true;
        state.counters.pa_flag_expire_h = state.clock_h + PA_FLAG_DUREE_H;
        state.counters.pa_derive_cooldown_h = state.clock_h + PA_COOLDOWN_H;
        // Aucun nouveau `kind` : on réutilise `warning`, déjà prévu pour la télégraphie
        // (SPEC-MOTEUR §1.8). L'énumération fermée reste fermée (§5).
        events.push({ kind: "warning", data: { derive: signalee, valeur, delta: derive } });
    }
    state.pa_reference = { h: state.clock_h, valeur };
    return signalee;
}
// ─────────────────────────────────────────────────────────────────────────────
// §6.1 — La condition d'éligibilité, pour `events.ts`
// ─────────────────────────────────────────────────────────────────────────────
/**
 * §6.1 — un événement portant `produit_min` ou `produit_max` passe-t-il ?
 *
 * **Inéligible quand l'axe vaut `null`** (✎) : un événement « il a changé » n'a aucun sens
 * avant que l'argent n'existe. C'est la même règle que `execution_min` sans vidéo publiée —
 * une condition inévaluable écarte le candidat, elle ne le laisse pas passer.
 *
 * Aucune borne ⇒ `true`, donc **tout le contenu d'avant l'axe se tire exactement comme
 * avant** : l'état neutre est obtenu par construction, pas par un réglage.
 */
export function conditionProduitPasse(state, bornes) {
    const { produit_min, produit_max } = bornes;
    if (produit_min === undefined && produit_max === undefined)
        return true;
    const valeur = axeProduit(state);
    if (valeur === null)
        return false;
    if (produit_min !== undefined && valeur < produit_min)
        return false;
    if (produit_max !== undefined && valeur > produit_max)
        return false;
    return true;
}
export function lectureAxeProduit(state) {
    const valeur = axeProduit(state);
    const derive = state.flags.derive_produit === true
        ? "produit"
        : state.flags.derive_artisanale === true
            ? "artisanale"
            : null;
    const echeance = state.counters.pa_flag_expire_h;
    return {
        bande: bandeProduit(valeur),
        valeur,
        derive,
        jours_avant_refroidissement: derive === null || echeance === undefined
            ? null
            : Math.max(0, Math.ceil((echeance - state.clock_h) / H_JOUR)),
    };
}
//# sourceMappingURL=axe-produit.js.map